import { ignoreAbortError } from '@/utils/abort-util'
import { type ScopeChangeRequestStore } from '@/features/admin/scope-change-requests/ScopeChangeRequestStore'
import { type ScopeChangeRequest } from './ScopeChangeRequest'
import type {
  GetCurrentUserWorkspaceMembershipsFn,
  ListWorkspaceMembershipsFn,
  GetCurrentMemberFn,
} from '@/features/iam'
import { type TableSortOrder } from '@/components/Table'
import { type FilterStorageFactory } from '@/infra/FiltersViewModel'
import { computed } from 'mobx'
import { task } from 'mobx-task'
import type { ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import { ViewModelList } from '@/infra/view-model-list'
import { ScopeChangeRequestsSearchViewModel } from './ScopeChangeRequestsSearchViewModel'
import { ScopeChangeRequestItemViewModel } from './ScopeChangeRequestItemViewModel'
import {
  type ScopeChangeRequestColumnDetails,
  ScopeChangeRequestTableColumns,
} from './scope-change-request-table-columns'
import { ScopeChangeRequestDetailsViewModel } from '@/features/admin/scope-change-requests/components/ScopeChangeRequestDetailsPanel/ScopeChangeRequestDetailsViewModel'
import { ScopeChangeRequestFinalizedDialogViewModel } from '@/features/admin/scope-change-requests/ScopeChangeRequestFinalizedDialog/ScopeChangeRequestFinalizedDialogViewModel'
import { ScopeChangeRequestResolveDialogViewModel } from '@/features/admin/scope-change-requests/ScopeChangeRequestResolveDialog/ScopeChangeRequestResolveDialogViewModel'
import { ScopeChangeRequestRejectDialogViewModel } from '@/features/admin/scope-change-requests/ScopeChangeRequestRejectDialog/ScopeChangeRequestRejectDialogViewModel'
import { type ListInventoryItemsFn } from '@/features/inventory'
import { type FetchJobsFn } from '@/features/jobs'
import type {
  AlertErrorHandlerFactory,
  AlertFactory,
  AlertRetryWrapperFactory,
} from '@/features/shell'

/**
 * Dependencies of the scope change requests page.
 */
export interface ScopeChangeRequestsViewModelOptions {
  searchInventoryItems: ListInventoryItemsFn
  scopeChangeRequestStore: ScopeChangeRequestStore
  listWorkspaceMemberships: ListWorkspaceMembershipsFn
  getWorkspaceMemberships: GetCurrentUserWorkspaceMembershipsFn
  createFilterStorage: FilterStorageFactory
  fetchJobs: FetchJobsFn
  getCurrentMember: GetCurrentMemberFn
  closeNav: () => void
  newAlert: AlertFactory
  createAlertErrorHandler: AlertErrorHandlerFactory
  createAlertRetryWrapper: AlertRetryWrapperFactory
}

export class ScopeChangeRequestsViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  private readonly _items = new ViewModelList({
    models: () => this.searcher.filteredItems.slice(),
    create: (scopeChangeRequest: ScopeChangeRequest) =>
      new ScopeChangeRequestItemViewModel(
        scopeChangeRequest,
        this.changeRequestDetailsVm,
      ),
  })

  readonly searcher

  readonly scopeChangeRequestFinalizedDialogVm =
    new ScopeChangeRequestFinalizedDialogViewModel()

  readonly scopeChangeRequestRejectOnBehalfOfClientDialogVm

  readonly scopeChangeRequestResolveDialogVm

  readonly scopeChangeRequestRejectDialogVm

  readonly changeRequestDetailsVm

  /**
   * Columns to populate the scope change requests table header and rows with.
   */
  readonly columns = ScopeChangeRequestTableColumns

  constructor(private readonly _options: ScopeChangeRequestsViewModelOptions) {
    const { scopeChangeRequestStore, createAlertErrorHandler } = _options
    const refreshStatusFilter = this.refreshStatusFilter.bind(this)

    this.searcher = new ScopeChangeRequestsSearchViewModel(
      scopeChangeRequestStore,
      _options.createAlertRetryWrapper,
      _options.getWorkspaceMemberships,
      _options.fetchJobs,
      _options.createFilterStorage('ChangeRequests'),
    )

    this.scopeChangeRequestRejectOnBehalfOfClientDialogVm =
      new ScopeChangeRequestRejectDialogViewModel(
        'RejectOnBehalfOfClient',
        scopeChangeRequestStore,
        refreshStatusFilter,
        createAlertErrorHandler,
      )

    this.scopeChangeRequestResolveDialogVm =
      new ScopeChangeRequestResolveDialogViewModel(
        scopeChangeRequestStore,
        this.scopeChangeRequestRejectOnBehalfOfClientDialogVm,
        refreshStatusFilter,
        createAlertErrorHandler,
      )

    this.scopeChangeRequestRejectDialogVm =
      new ScopeChangeRequestRejectDialogViewModel(
        'Decline',
        scopeChangeRequestStore,
        refreshStatusFilter,
        createAlertErrorHandler,
      )

    this.changeRequestDetailsVm = new ScopeChangeRequestDetailsViewModel({
      searchInventoryItems: _options.searchInventoryItems,
      scopeChangeRequestStore,
      finalizedDialogVm: this.scopeChangeRequestFinalizedDialogVm,
      resolveDialogVm: this.scopeChangeRequestResolveDialogVm,
      rejectDialogVm: this.scopeChangeRequestRejectDialogVm,
      getCurrentMember: _options.getCurrentMember,
      refreshStatusFilter,
      closeNav: _options.closeNav,
      newAlert: _options.newAlert,
      createAlertErrorHandler,
      createAlertRetryWrapper: _options.createAlertRetryWrapper,
    })

    this._lifecycle.manage(this._items)
    this._lifecycle.manage(this.searcher)
    this._lifecycle.manage(this.changeRequestDetailsVm)
  }

  @computed
  get columnIds() {
    return this.columns.map((column) => column.id)
  }

  /**
   * The table rows.
   */
  get items() {
    return this._items.items
  }

  /**
   * Refresh status filter
   */
  refreshStatusFilter() {
    return this.searcher.refreshStatusFilter()
  }

  /**
   * Gets the sort order for the specified column.
   *
   * @param column
   */
  getSortOrder(column: ScopeChangeRequestColumnDetails): TableSortOrder {
    return this.searcher.sorting.field === column.field
      ? this.searcher.sorting.direction
      : 'none'
  }

  /**
   * Sets the sort order for the specified column.
   *
   * @param column
   * @param direction
   */
  setSortOrder(
    column: ScopeChangeRequestColumnDetails,
    direction: TableSortOrder,
  ) {
    if (!column.field) {
      return
    }

    this.searcher.setSortField(column.field)
    this.searcher.setSortDirection(direction)
  }

  /**
   * Loads the workspace memberships the searcher filters by.
   */
  fetch = task(async () => {
    await this._options.listWorkspaceMemberships({}, this._lifecycle.signal)
  })

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
    void this.fetch().catch(ignoreAbortError())
  }

  /** @inheritDoc */
  deactivate() {
    this._lifecycle.deactivate()
  }
}
